import boxOpenIcon from "@loot-os/icons/icons/box-open";
import tcgIcon from "@loot-os/icons/icons/tcg";

import { Icon } from "../core/Icon.js";
import { cn } from "../foundations/cn.js";

export interface ArtPlaceholderProps {
  /** What the missing art was of: a box's pack, or an item's card. */
  kind: "box" | "item";
  /** The art's own name, read in its place; empty when the name is written beside it. */
  label?: string;
  className?: string;
}

/**
 * Drawn in place of a box's or an item's art that is missing or failed to load: a pack's or a
 * card's silhouette in the theme's raised fill and subtle edge, its icon at the center. It takes
 * the art's place and size, so a card or row keeps its shape.
 */
export function ArtPlaceholder({ kind, label = "", className }: ArtPlaceholderProps) {
  const box = kind === "box";
  return (
    <span
      data-placeholder={kind}
      className={cn("relative flex h-full w-full items-center justify-center", className)}
    >
      <svg
        viewBox={box ? "0 0 100 140" : "0 0 63 88"}
        className="h-full max-h-full w-auto max-w-full fill-bg-raised stroke-border-default"
        role="img"
        aria-hidden={label ? undefined : "true"}
      >
        <title>{label}</title>
        {box ? (
          <>
            {/* A pack: its body, and the crimped seals at the top and bottom. */}
            <rect x="10" y="12" width="80" height="116" rx="5" strokeWidth="1.5" />
            <path d="M8 6h84v10H8zM8 124h84v10H8z" strokeWidth="1.5" className="fill-bg-inset" />
            <path
              d="M18 6v10M28 6v10M38 6v10M48 6v10M58 6v10M68 6v10M78 6v10M18 124v10M28 124v10M38 124v10M48 124v10M58 124v10M68 124v10M78 124v10"
              strokeWidth="1"
              fill="none"
            />
          </>
        ) : (
          <>
            {/* A card's back: its edge, and the inner frame. */}
            <rect x="1" y="1" width="61" height="86" rx="3" strokeWidth="1.2" />
            <rect x="6" y="6" width="51" height="76" rx="2" strokeWidth="0.8" fill="none" />
          </>
        )}
      </svg>
      <span aria-hidden className="absolute text-text-tertiary">
        <Icon icon={box ? boxOpenIcon : tcgIcon} size={box ? 24 : 20} />
      </span>
    </span>
  );
}
